import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useQuery } from '@tanstack/react-query';
import * as artifactService from '../../services/artifactService';
import { ArtifactRelationshipType } from '../../types/artifact';
import ArtifactNode from '../graphs/NodeTypes/ArtifactNode';
import { useTheme } from '../../contexts/ThemeContext';

interface ArtifactDependencyGraphProps {
  artifactId: string;
  height?: string;
  onNodeClick?: (artifactId: string) => void;
}

const nodeTypes = {
  artifact: ArtifactNode
};

// Map relationship types to visual properties
const relationshipTypeConfig: Record<ArtifactRelationshipType, {
  label: string;
  color: string;
  strokeWidth: number;
  markerEnd: { type: MarkerType; color: string };
}> = {
  [ArtifactRelationshipType.DERIVED_FROM]: {
    label: 'Derived From',
    color: '#3b82f6', // blue
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
  },
  [ArtifactRelationshipType.REFERENCES]: {
    label: 'References',
    color: '#10b981', // green
    strokeWidth: 1,
    markerEnd: { type: MarkerType.Arrow, color: '#10b981' }
  },
  [ArtifactRelationshipType.INSPIRES]: {
    label: 'Inspires',
    color: '#f59e0b', // amber
    strokeWidth: 1.5,
    markerEnd: { type: MarkerType.Arrow, color: '#f59e0b' }
  },
  [ArtifactRelationshipType.CRITIQUES]: {
    label: 'Critiques',
    color: '#ef4444', // red
    strokeWidth: 1.5,
    markerEnd: { type: MarkerType.Arrow, color: '#ef4444' }
  },
  [ArtifactRelationshipType.IMPLEMENTS]: {
    label: 'Implements',
    color: '#8b5cf6', // purple
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' }
  },
  [ArtifactRelationshipType.DEPENDS_ON]: {
    label: 'Depends On',
    color: '#6b7280', // gray
    strokeWidth: 1,
    markerEnd: { type: MarkerType.Arrow, color: '#6b7280' }
  },
  [ArtifactRelationshipType.SUPERSEDES]: {
    label: 'Supersedes',
    color: '#ec4899', // pink
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899' }
  }
};

export const ArtifactDependencyGraph: React.FC<ArtifactDependencyGraphProps> = ({
  artifactId,
  height = '600px',
  onNodeClick
}) => {
  const { darkMode } = useTheme();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [depth, setDepth] = useState(2);
  const [relationshipTypes, setRelationshipTypes] = useState<ArtifactRelationshipType[]>(
    Object.values(ArtifactRelationshipType)
  );

  const { data: graphData, isLoading, error } = useQuery(
    ['artifactGraph', artifactId, depth, relationshipTypes],
    () => artifactService.getArtifactDependencyGraph(artifactId, depth, relationshipTypes),
    { enabled: !!artifactId }
  );

  useEffect(() => {
    if (graphData) {
      // Transform the graph data into ReactFlow nodes and edges
      const flowNodes: Node[] = graphData.nodes.map((node: any) => ({
        id: node.id,
        type: 'artifact',
        position: node.position || { x: 0, y: 0 },
        data: {
          label: node.name,
          type: node.type,
          creator: node.agent_name,
          version: node.version,
          preview: node.preview
        }
      }));

      const flowEdges: Edge[] = graphData.edges.map((edge: any) => {
        const config = relationshipTypeConfig[edge.type as ArtifactRelationshipType] || {
          label: edge.type,
          color: '#888',
          strokeWidth: 1,
          markerEnd: { type: MarkerType.Arrow, color: '#888' }
        };

        return {
          id: edge.id,
          source: edge.source_id,
          target: edge.target_id,
          label: config.label,
          type: 'smoothstep',
          animated: edge.type === ArtifactRelationshipType.DERIVED_FROM,
          style: { stroke: config.color, strokeWidth: config.strokeWidth },
          markerEnd: config.markerEnd
        };
      });

      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [graphData, setNodes, setEdges]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (onNodeClick) {
        onNodeClick(node.id);
      }
    },
    [onNodeClick]
  );

  const handleRelationshipTypeChange = (type: ArtifactRelationshipType) => {
    setRelationshipTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  if (isLoading) {
    return <div className={`flex justify-center items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ height }}>Loading dependency graph...</div>;
  }

  if (error) {
    return <div className={`text-red-500 text-center ${darkMode ? 'text-red-400' : ''}`} style={{ height }}>Error loading dependency graph</div>;
  }

  if (!isLoading && !error && (!graphData || graphData.nodes.length === 0)) {
    return (
      <div className={`flex justify-center items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ height }}>
        No relationships found for this artifact.
      </div>
    );
  }

  return (
    <div className="artifact-dependency-graph">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="mr-4">
          <label className={`mr-2 text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>Depth:</label>
          <select
            value={depth}
            onChange={e => setDepth(Number(e.target.value))}
            className={`border rounded px-2 py-1 text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value={1}>1 Level</option>
            <option value={2}>2 Levels</option>
            <option value={3}>3 Levels</option>
            <option value={4}>4 Levels</option>
            <option value={5}>5 Levels</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-1">
          {Object.entries(relationshipTypeConfig).map(([type, config]) => (
            <button
              key={type}
              className={`px-2 py-1 text-xs rounded-full flex items-center ${
                relationshipTypes.includes(type as ArtifactRelationshipType)
                  ? darkMode 
                    ? 'bg-gray-700 text-gray-200' 
                    : 'bg-gray-200 text-gray-800'
                  : darkMode 
                    ? 'bg-gray-800 text-gray-500' 
                    : 'bg-gray-100 text-gray-400'
              }`}
              onClick={() => handleRelationshipTypeChange(type as ArtifactRelationshipType)}
              style={{
                borderColor: config.color,
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <span
                className="w-2 h-2 rounded-full mr-1"
                style={{ backgroundColor: config.color }}
              ></span>
              {config.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
        >
          <Controls />
          <MiniMap style={{ background: darkMode ? '#1f2937' : '#f9fafb' }} />
          <Background color={darkMode ? '#374151' : '#aaaaaa'} />
        </ReactFlow>
      </div>
    </div>
  );
};
